import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';
import {
  DataMultiSelect,
  DataMultiSelectInheritedProps,
  DataSelect,
  DataSelectInheritedProps,
} from 'components/selects/DataSelect';
import { useApi } from 'api/api-context';

interface PriceOfferProviderSelectProps extends DataSelectInheritedProps {
  departmentId?: number;
}

interface PriceOfferProviderMultiSelectProps extends DataMultiSelectInheritedProps {
  departmentId?: number;
}

/**
 * Fetches users who can provide price offers in a department.
 */
function usePriceOfferProviders(departmentId?: number) {
  const { getAction } = useApi();
  const key = useMemo(() => nanoid(), [departmentId]);

  const fetchProviders = useCallback(async () => {
    if (!departmentId) {
      return [];
    }

    const action = getAction('PriceOfferListProviders');
    const providers = await action({ query: { departmentId: String(departmentId) } });

    return providers
      .map(({ userId, fullName, organization }) => ({
        value: String(userId),
        label: fullName,
        group: organization.name,
      }))
      .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
  }, [departmentId, getAction]);

  return { key, fetchProviders };
}

/**
 * Selects users who can provide price offers in a department.
 */
export default function PriceOfferProviderSelect({ departmentId, ...props }: PriceOfferProviderSelectProps) {
  const { key, fetchProviders } = usePriceOfferProviders(departmentId);

  return (
    <DataSelect
      key={key}
      valueProp="value"
      labelProp="label"
      groupProp="group"
      action={fetchProviders}
      disabled={!departmentId || props.disabled}
      {...props}
    />
  );
}

/**
 * Selects multiple users who can provide price offers in a department.
 */
export function PriceOfferProviderMultiSelect({ departmentId, ...props }: PriceOfferProviderMultiSelectProps) {
  const { key, fetchProviders } = usePriceOfferProviders(departmentId);

  return (
    <DataMultiSelect
      key={key}
      valueProp="value"
      labelProp="label"
      groupProp="group"
      action={fetchProviders}
      disabled={!departmentId || props.disabled}
      {...props}
    />
  );
}
